// /sports/super-admin/devices/:id — one phone: live state, the tenant link,
// the court's public link and staff-PIN status, the timeline (server-side
// events: paired / linked / offline / online / staff PIN / revoked / command /
// forced end), the Command bar (Phase 3: force end, out of service, rate,
// share, court name, staff PIN, diagnostics) and the command history table.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Smartphone, Pencil, MapPin, Ban, Link2, RefreshCw, Radio, WifiOff, Wifi, KeyRound, Terminal, FileText, Building2, Unlink, RotateCcw, HandCoins, AlertOctagon, CheckCircle, OctagonX } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';
import { SuperAdminShell } from './SportsSuperAdminPage';
import { getDevice, pingDevice, listTenants, updateDevice, revokeDevice, rotateCourtLink, getCollectPlan, commitCollect, getDeviceStatements, getDeviceStatement, isAdminDenied, errMsg, errDetail, epochDayToYmd } from '../kiosk/kioskApi';
import { StatementModal } from '../kiosk/KioskStatement';
import {
  RED, GREEN, GREY, AMBER, OnlineDot, StateBadge, PairDeviceDialog, TextPromptModal, TenantSelect, CourtLinkCell, StaffPinStatus,
  AdminDeniedNotice, SyncBadge, fmtMs, fmtAgo, fmtDuration, fmtCentavos, deviceState, labelStyle, inputStyle,
} from '../kiosk/KioskDialogs';
import { CommandBar, CommandHistory, describePush } from '../kiosk/KioskCommands';
import { LiveCard } from '../kiosk/KioskLive';
import { fmtHrMin, fmtBookedPlayed } from '../liveUi';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';

import { BillingPanel } from '../kiosk/KioskBilling';
const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 18 };
const iconBtn = {
  background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: '7px 10px', color: GREY, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
};

const EVENT_META = {
  offline: { Icon: WifiOff, color: '#FF2D55', label: 'Went offline' },
  online: { Icon: Wifi, color: GREEN, label: 'Back online' },
  paired: { Icon: Link2, color: '#fff', label: 'Paired' },
  revoked: { Icon: Ban, color: GREY, label: 'Revoked' },
  linked: { Icon: Building2, color: GREEN, label: 'Linked to tenant' },
  unlinked: { Icon: Unlink, color: AMBER, label: 'Unlinked from tenant' },
  staff_pin: { Icon: KeyRound, color: '#fff', label: 'Staff PIN updated' },
  court_link_rotated: { Icon: RotateCcw, color: AMBER, label: 'Court link replaced' },
  collected: { Icon: HandCoins, color: GREEN, label: 'Collected on the web' },
  collection_acked: { Icon: CheckCircle, color: GREEN, label: 'Phone applied the web settlement' },
  conflict: { Icon: AlertOctagon, color: '#FF2D55', label: 'Double settlement' },
  command: { Icon: Terminal, color: '#fff', label: 'Command issued' },
  forced_end: { Icon: OctagonX, color: '#FF2D55', label: 'Forced end applied by the phone' },
};

function Fact({ label, value, testId, color = '#fff' }) {
  return (
    <div>
      <p style={labelStyle}>{label}</p>
      <p data-testid={testId} style={{ fontSize: 14, color, marginTop: 3, fontWeight: 600 }}>{value}</p>
    </div>
  );
}

const STOP_LABEL = {
  WEATHER: 'Rain or weather', MACHINE_FAULT: 'Machine problem', KIOSK_FAULT: 'Kiosk problem',
  GOODWILL: 'Goodwill', MISTAKE: 'Started by mistake', OTHER: 'Owner override',
};
const hhmm = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const dmd = (ms) => (ms ? new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—');
const BLOCK_TEXT = {
  session_running: 'A session in this period is still running — the period is not over.',
  rows_pending: 'The phone still has session rows to upload; one of them could belong to this period. Wait for it to sync.',
  stale_sync: 'The phone hasn’t synced recently, so it may hold rentals the server hasn’t received. Bring it online and wait for it to sync.',
};

// The web collection (K6 P2): the SAME plan the phone would build — day after
// the last settlement (web or phone) to yesterday, blocked by a running
// session or by rows the phone has not uploaded — committed with the admin
// login instead of the owner PIN. The phone picks it up on its next heartbeat.
function CollectPanel({ device, onCollected }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');          // 7.0e: authenticator code or the phone's owner PIN
  const [confirm, setConfirm] = useState(0);     // 0 none · 1 first ask · 2 second ask (phone offline / behind only)
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setError('');
    return getCollectPlan(device.kiosk_id).then(setData).catch(e => setError(errMsg(e, 'Could not build the plan')));
  }, [device.kiosk_id]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const plan = data?.plan;
  // 2026-09-30: a phone that is offline or behind blocks a normal collection, but the owner may still
  // collect (lost / stolen phone) after TWO confirmations + the code. Any other blocker stays hard.
  const staleOnly = !!plan?.blocked && plan.blocked_reasons.every(r => r === 'stale_sync') && !!plan.sync_guard?.can_override;
  const hardBlocked = !!plan?.blocked && !staleOnly;
  const commit = async () => {
    setConfirm(0);
    setBusy(true);
    try {
      const r = await commitCollect(device.kiosk_id, {
        expected_gross_centavos: plan.gross_centavos,
        expected_from_epoch_day: plan.period_from_epoch_day,
        expected_to_epoch_day: plan.period_to_epoch_day,
        note: note.trim() || undefined,
        owner_code: code.trim(),
        ...(staleOnly ? { accept_stale: true } : {}),
      });
      toast.success(`Collected ${plan.period_from} to ${plan.period_to}: ${fmtCentavos(r.collection.gross_centavos)} gross. The phone applies it on its next heartbeat.`);
      setNote('');
      setCode('');
      setOpen(false);
      load();
      onCollected?.();
    } catch (e) {
      const d = errDetail(e);
      toast.error((d && d.message) || errMsg(e, 'Could not collect'));
      load();
    } finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, marginBottom: 16 }} data-testid="kiosk-collect-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Collect</h2>
          <p style={{ fontSize: 12, color: GREY, marginTop: 2, maxWidth: 560, lineHeight: 1.5 }}>
            The settlement the phone would offer right now, built from its mirrored rows. Owner and court figures are derived here with the phone&apos;s split rule.
          </p>
        </div>
        <button onClick={load} data-testid="kiosk-collect-refresh" style={iconBtn}><RefreshCw className="h-4 w-4" /></button>
      </div>

      {error ? <p style={{ color: '#FF2D55', fontSize: 13, marginTop: 12 }}>{error}</p>
        : !data ? <p style={{ color: GREY, fontSize: 13, marginTop: 12 }}>Building the plan…</p>
        : !plan ? (
          <p style={{ color: GREY, fontSize: 13, marginTop: 12 }} data-testid="kiosk-collect-nothing">
            Nothing to collect — no finished day is due{data.today ? ` (today is ${data.today}, and today is never collectable)` : ''}.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
              <Fact label="Period" value={`${plan.period_from} → ${plan.period_to}`} testId="kiosk-collect-period" />
              <Fact label="Collected at court (gross)" value={fmtCentavos(plan.gross_centavos)} testId="kiosk-collect-gross" />
              <Fact label="Owner side (derived)" value={fmtCentavos(plan.owner_centavos)} color={AMBER} testId="kiosk-collect-owner" />
              <Fact label="Court side (derived)" value={fmtCentavos(plan.court_centavos)} color={plan.court_centavos < 0 ? '#FF2D55' : GREEN} testId="kiosk-collect-court" />
            </div>
            <p style={{ fontSize: 12, color: GREY, marginTop: 8 }}>
              {/* T1: booked and played, never the money-derived "billed h" (left out until the server sends both). */}
              {plan.session_count} rental{plan.session_count === 1 ? '' : 's'}
              {plan.booked_minutes != null && plan.played_minutes != null
                ? <span data-testid="kiosk-collect-booked-played"> · {fmtHrMin(plan.booked_minutes)} booked · {fmtHrMin(plan.played_minutes)} played</span>
                : null}
              {' · '}{plan.charged_count} charged · {plan.void_count} not charged · {plan.pro_rated_count} pro-rated
              {plan.court_comp_owed_centavos ? ` · court owes ${fmtCentavos(plan.court_comp_owed_centavos)} on its comps` : ''}
              {plan.owner_comp_owed_centavos ? ` · owner owes ${fmtCentavos(plan.owner_comp_owed_centavos)} on their comps` : ''}
              {plan.review_count ? ` · ${plan.review_count} to review` : ''}
              {plan.underivable_count ? ` · ${plan.underivable_count} row(s) not derivable` : ''}
              {plan.last_collected_through ? ` · last settled through ${plan.last_collected_through}` : ' · first settlement'}
            </p>
            {plan.court_centavos < 0 && <p style={{ fontSize: 12, color: AMBER, marginTop: 4 }}>Negative court side: the court owes the owner this on top of handing over the cash.</p>}
            {plan.blocked && plan.blocked_reasons.map(r => (r === 'stale_sync' && staleOnly ? (
              <p key={r} data-testid="kiosk-collect-stale-warning" style={{ fontSize: 13, color: AMBER, marginTop: 8, fontWeight: 600, lineHeight: 1.5 }}>
                Warning: {plan.sync_guard?.message || BLOCK_TEXT[r]} If the phone is lost or stolen, you can still collect what the server holds — you'll be asked twice, and rentals still on the phone won't be included.
              </p>
            ) : (
              <p key={r} data-testid={`kiosk-collect-blocked-${r}`} style={{ fontSize: 13, color: '#FF2D55', marginTop: 8, fontWeight: 600 }}>Blocked: {(r === 'stale_sync' && plan.sync_guard?.message) || BLOCK_TEXT[r] || r}</p>
            )))}

            <button onClick={() => setOpen(v => !v)} data-testid="kiosk-collect-toggle-list" style={{ ...iconBtn, marginTop: 12 }}>
              {open ? 'Hide' : 'Show'} the {plan.session_count} rental{plan.session_count === 1 ? '' : 's'}
            </button>
            {open && (
              <div style={{ overflowX: 'auto', marginTop: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }} data-testid="kiosk-collect-list">
                  <thead><tr style={{ borderBottom: '1px solid #1E1E1E' }}>
                    {['When', 'Site', 'Time', 'Ended', 'Charged', 'Owner', 'Court'].map((h, i) => <th key={h} style={{ ...labelStyle, textAlign: i < 2 ? 'left' : 'right', padding: '8px 10px', fontWeight: 700 }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {plan.sessions.map(s => {
                      const R = { padding: '8px 10px', fontSize: 12, textAlign: 'right' };
                      return (
                        <tr key={s.row_id} data-testid={`kiosk-collect-row-${s.row_id}`} style={{ borderBottom: '1px solid #1E1E1E' }}>
                          <td style={{ padding: '8px 10px', fontSize: 12 }}>{dmd(s.started_at_ms)} {hhmm(s.started_at_ms)}–{hhmm(s.ended_at_ms)}
                            {(s.customer_name || s.trial) && <div style={{ color: GREY, fontSize: 11 }} data-testid={`kiosk-collect-name-${s.row_id}`}>{s.customer_name || ''}{s.customer_name && s.trial ? ' · ' : ''}{s.trial ? <span style={{ color: GREEN }}>free trial</span> : null}</div>}
                          </td>
                          <td style={{ padding: '8px 10px', fontSize: 12, color: GREY }}>{s.site_name || '—'}</td>
                          {/* T1: "2 hrs" / "2 hrs booked · 1 min played" / "open · 45 min"; the old figure until the server sends both. */}
                          <td style={{ ...R, whiteSpace: 'nowrap' }} data-testid={`kiosk-collect-time-${s.row_id}`}>{fmtBookedPlayed(s.booked_minutes, s.played_minutes, s.mode) ?? fmtHrMin(s.billed_minutes ?? s.minutes)}</td>
                          <td style={{ ...R, color: s.stop_reason ? AMBER : GREY }}>{s.stop_reason ? (STOP_LABEL[s.stop_reason] || s.stop_reason) : 'normal'}{s.bearer && s.bearer !== 'NOBODY' ? ` (${s.bearer.toLowerCase()})` : ''}</td>
                          <td style={{ ...R, fontWeight: 700 }}>{fmtCentavos(s.charged_centavos)}</td>
                          <td style={{ ...R, color: AMBER }}>{s.derived ? fmtCentavos(s.owner_share_centavos) : '?'}</td>
                          <td style={{ ...R, color: s.court_side_centavos < 0 ? '#FF2D55' : GREEN }}>{s.derived ? fmtCentavos(s.court_side_centavos) : '?'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-end gap-2 mt-4 flex-wrap">
              <div style={{ flex: 1, minWidth: 220 }}>
                <p style={labelStyle}>Note (optional)</p>
                <input value={note} onChange={e => setNote(e.target.value.slice(0, 500))} data-testid="kiosk-collect-note" placeholder="e.g. cash handed over at the counter"
                  style={{ ...inputStyle, marginTop: 4 }} />
              </div>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))} data-testid="kiosk-collect-code" inputMode="numeric" autoComplete="one-time-code"
                placeholder="Authenticator code or the phone's owner PIN"
                style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', width: '100%', maxWidth: 360, outline: 'none', fontSize: 14, marginTop: 8, letterSpacing: 2 }} />
              <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Proves the collection is intentional: the six digits from your authenticator, or the owner PIN set on this phone. Five wrong tries lock it for 15 minutes.</p>
              <button onClick={() => setConfirm(1)} disabled={hardBlocked || plan.is_empty || busy || device.status !== 'active' || code.trim().length < 4} data-testid="kiosk-collect-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: hardBlocked || plan.is_empty ? '#2A2A2A' : staleOnly ? AMBER : RED, color: staleOnly ? '#000' : '#fff', border: 'none',
                  borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5,
                  cursor: hardBlocked || plan.is_empty ? 'not-allowed' : 'pointer' }}>
                <HandCoins className="h-4 w-4" /> {busy ? 'Collecting…' : staleOnly ? 'Collect anyway' : 'Collect'}
              </button>
            </div>
          </>
        )}

      {confirm === 1 && plan && !staleOnly && (
        <ConfirmModal title={`Collect ${plan.period_from} → ${plan.period_to}?`} confirmLabel="Collect" color={RED}
          message={`Your owner PIN commits this on the phone; here your admin login plus the code you typed do. ${plan.session_count} rental(s), gross ${fmtCentavos(plan.gross_centavos)} — owner ${fmtCentavos(plan.owner_centavos)}, court ${fmtCentavos(plan.court_centavos)} (derived). The server re-checks the plan first and refuses if it moved; the phone records the same settlement on its next heartbeat.`}
          onConfirm={commit} onCancel={() => setConfirm(0)} />
      )}
      {confirm === 1 && plan && staleOnly && (
        <ConfirmModal title="The phone is offline or behind" confirmLabel="I understand, continue" color={AMBER}
          message={`${plan.sync_guard?.message || ''} Collecting now settles only the ${plan.session_count} rental(s) the server has (gross ${fmtCentavos(plan.gross_centavos)}). Any rental still on the phone will NOT be in this settlement and will not be picked up by a later one. Use this only if the phone is lost, stolen or won't come back online.`}
          onConfirm={() => setConfirm(2)} onCancel={() => setConfirm(0)} />
      )}
      {confirm === 2 && plan && staleOnly && (
        <ConfirmModal title={`Collect anyway: ${plan.period_from} → ${plan.period_to}?`} confirmLabel="Yes, collect anyway" color={RED}
          message={`Second confirmation. ${plan.session_count} rental(s), gross ${fmtCentavos(plan.gross_centavos)} — owner ${fmtCentavos(plan.owner_centavos)}, court ${fmtCentavos(plan.court_centavos)} (derived). The settlement will be marked "collected while the phone was offline".`}
          onConfirm={commit} onCancel={() => setConfirm(0)} />
      )}
    </div>
  );
}

// 7.0f: every settlement of this phone — web and phone alike, newest first,
// the phone's echo of a web one folded onto the web entry — each with its
// statement (owner audience). `refreshKey` bumps after a web collection.
function StatementsSection({ kioskId, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);      // { ref, title, load }

  const load = useCallback(() => {
    setError('');
    return getDeviceStatements(kioskId).then(setData).catch(e => setError(errMsg(e, 'Could not load the statements')));
  }, [kioskId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const show = (s) => setOpen({
    ref: s.id,
    title: `Statement · ${s.period_from} → ${s.period_to}`,
    load: () => getDeviceStatement(kioskId, s.id),
  });

  const items = data?.statements || [];
  return (
    <div style={{ ...card, marginBottom: 16 }} data-testid="kiosk-device-statements">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Statements</h2>
          <p style={{ fontSize: 12, color: GREY, marginTop: 2, maxWidth: 560, lineHeight: 1.5 }}>
            One per settlement, whether it was made on the phone or here. The court sees the same document without HighMark&apos;s figure.
            {data?.settled_through ? ` Settled through ${data.settled_through}.` : ''}
          </p>
        </div>
        <button onClick={load} data-testid="kiosk-statements-refresh" style={iconBtn}><RefreshCw className="h-4 w-4" /></button>
      </div>

      {error ? <p style={{ color: '#FF2D55', fontSize: 13, marginTop: 12 }}>{error}</p>
        : !data ? <p style={{ color: GREY, fontSize: 13, marginTop: 12 }}>Loading…</p>
        : items.length === 0 ? <p style={{ color: '#555', fontSize: 13, marginTop: 12 }} data-testid="kiosk-statements-none">No settlement yet.</p>
        : (
          <div style={{ overflowX: 'auto', marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }} data-testid="kiosk-statements-list">
              <thead><tr style={{ borderBottom: '1px solid #1E1E1E' }}>
                {['Period', 'Settled', 'Rentals', 'Gross', 'Owner', 'Court', 'Phone', ''].map((h, i) => (
                  <th key={h || 'action'} style={{ ...labelStyle, textAlign: i >= 2 && i <= 5 ? 'right' : 'left', padding: '8px 10px', fontWeight: 700 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.map(s => {
                  const R = { padding: '8px 10px', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' };
                  return (
                    <tr key={s.id} data-testid={`kiosk-statement-row-${s.id}`} style={{ borderBottom: '1px solid #1E1E1E' }}>
                      <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {s.period_from} → {s.period_to}
                        {s.conflict && <span style={{ color: '#FF2D55', fontWeight: 800, marginLeft: 8, fontSize: 10, textTransform: 'uppercase' }}>conflict</span>}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: GREY }}>
                        <span style={{ color: '#ccc' }}>{s.source === 'phone' ? 'on the phone' : `on the web${s.by ? ` by ${s.by}` : ''}`}</span>
                        {' · '}{fmtMs(s.collected_at_ms)}
                        {s.note ? <div style={{ fontSize: 11, color: '#555', whiteSpace: 'normal' }}>{s.note}</div> : null}
                      </td>
                      <td style={R}>{s.session_count ?? '—'}</td>
                      <td style={{ ...R, fontWeight: 700 }}>{fmtCentavos(s.gross_centavos)}</td>
                      <td style={{ ...R, color: AMBER }}>{fmtCentavos(s.owner_centavos)}</td>
                      <td style={{ ...R, color: s.court_centavos < 0 ? '#FF2D55' : GREEN }}>{fmtCentavos(s.court_centavos)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: s.acked_by_phone ? GREEN : AMBER, whiteSpace: 'nowrap' }}>
                        {s.acked_by_phone ? 'applied' : 'pending'}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                        <button onClick={() => show(s)} data-testid={`kiosk-statement-open-${s.id}`} style={{ ...iconBtn, padding: '5px 9px' }}><FileText className="h-3.5 w-3.5" /> Statement</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {open && <StatementModal title={open.title} load={open.load} audience="owner" onClose={() => setOpen(null)} />}
    </div>
  );
}

export default function SportsKioskDevicePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState(null);    // 'denied' | 'notfound' | string
  const [now, setNow] = useState(Date.now());
  const [prompt, setPrompt] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [ownerKey, setOwnerKey] = useState('');            // 7.0e(g): the owner PIN or authenticator code typed into the confirm box
  const [rePair, setRePair] = useState(false);
  const [linking, setLinking] = useState(false);
  const [statementsKey, setStatementsKey] = useState(0);   // 7.0f: bumps after a web collection so the list refetches
  const [checking, setChecking] = useState(false);         // 7.6: "Check now" sent, waiting for the phone's answer
  const checkTimer = useRef(null);                          // 7.6: the 2-s poll; cleared on answer, timeout or unmount

  const load = useCallback((silent = false) => {
    return getDevice(id)
      .then(d => { setData(d); setError(null); })
      .catch(e => {
        if (isAdminDenied(e)) setError('denied');
        else if (e?.response?.status === 404) setError('notfound');
        else if (!silent) setError(errMsg(e, 'Could not load this device'));
      })
      .finally(() => setNow(Date.now()));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listTenants().then(t => setTenants(Array.isArray(t) ? t : [])).catch(() => {}); }, []);
  useAutoRefresh(() => load(true));
  useEffect(() => () => clearInterval(checkTimer.current), []);   // 7.6: never poll a page that is gone

  // 7.6: the owner's "Check now", the Management App's button on the web. A content-free
  // nudge; when push reaches the phone it answers with a full heartbeat within seconds,
  // so we watch last_seen move past the ping for up to 20 s (every 2 s). When Google did
  // not take the nudge there is nothing to wait for — the phone keeps its own beat and
  // the 30-s auto refresh underneath shows it when it lands.
  const stopChecking = () => { clearInterval(checkTimer.current); checkTimer.current = null; setChecking(false); };
  const checkNow = async () => {
    let r;
    try { r = await pingDevice(id); }
    catch (e) { const dd = errDetail(e); toast.warning((dd && dd.message) || errMsg(e, 'Could not nudge the phone')); return; }   // 429 too_soon / 409 revoked carry their own words
    if (!r.sent) { toast.warning(r.message || 'The nudge did not reach Google; the phone keeps its own beat.'); return; }
    toast.info(r.message || 'Nudged — watch for the phone\'s answer.');
    const pingedAt = r.pinged_at_ms || Date.now();
    const started = Date.now();
    clearInterval(checkTimer.current);
    setChecking(true);
    checkTimer.current = setInterval(async () => {
      let seen = 0;
      try { const dd = await getDevice(id); seen = dd?.device?.last_seen_ms || 0; } catch { /* a missed poll is not an answer */ }
      if (!checkTimer.current) return;                                 // stopped (answered, timed out or unmounted) while this poll was in flight
      if (seen > pingedAt) { stopChecking(); toast.success('Answered — last seen just now'); load(true); }
      else if (Date.now() - started >= 20000) { stopChecking(); toast.warning('No answer yet — the phone keeps its own beat'); }
    }, 2000);
  };

  const d = data?.device;
  const state = d ? deviceState(d) : null;

  const saveField = async (field, value) => {
    await updateDevice(id, { [field]: value });
    toast.success(field === 'name' ? 'Renamed' : field === 'site_name' ? 'Site updated' : 'Notes saved');
    load(true);
  };

  const setTenant = async (orgId) => {
    setLinking(true);
    try {
      const r = await updateDevice(id, { court_org_id: orgId || null });
      toast.success(orgId ? `Linked to ${r.court_name || 'tenant'}` : 'Unlinked — public link only');
      load(true);
    } catch (e) { toast.error(errMsg(e, 'Could not change the tenant link')); }
    finally { setLinking(false); }
  };

  const doRevoke = async () => {
    setConfirmRevoke(false);
    const key = ownerKey; setOwnerKey('');
    try { await revokeDevice(id, { owner_code: key }); toast.success('Revoked — the phone will show "not paired"'); load(true); }
    catch (e) { const dd = errDetail(e); toast.error((dd && dd.message) || errMsg(e, 'Revoke failed')); }
  };

  const doRotate = async () => {
    setConfirmRotate(false);
    const key = ownerKey; setOwnerKey('');
    try { const r = await rotateCourtLink(id, { owner_code: key }); toast.success(`New court link: ${r.code}. The old link no longer opens.`); load(true); }
    catch (e) { const dd = errDetail(e); toast.error((dd && dd.message) || errMsg(e, 'Rotate failed')); }
  };

  return (
    <SuperAdminShell title={d ? d.name : 'Device'} active="devices">
      <main style={{ padding: '20px 16px', maxWidth: 1040 }} data-testid="sports-kiosk-device-page">
        <Link to="/sports/super-admin/devices" data-testid="kiosk-back" className="inline-flex items-center gap-1.5 mb-4" style={{ color: GREY, fontSize: 13, textDecoration: 'none' }}>
          <ArrowLeft className="h-4 w-4" /> All devices
        </Link>

        {error === 'denied' ? <AdminDeniedNotice /> : error === 'notfound' ? (
          <div style={{ ...card, textAlign: 'center', padding: 60 }}>
            <Smartphone className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
            <p style={{ color: GREY }}>No device with this id.</p>
          </div>
        ) : error ? (
          <p style={{ color: '#FF2D55' }}>{error}</p>
        ) : !d ? (
          <p style={{ color: GREY, textAlign: 'center', padding: 40 }}>Loading…</p>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ background: RED + '18', color: RED }}>
                  <Smartphone className="h-5 w-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <OnlineDot device={d} size={12} />
                    <h1 data-testid="kiosk-device-name" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>{d.name}</h1>
                    <StateBadge device={d} />
                  </div>
                  <p style={{ fontSize: 12, color: GREY, marginTop: 2 }}>
                    {d.site_name ? <span style={{ color: '#fff' }}>{d.site_name}</span> : 'No site set'}
                    {' · '}{state.key === 'offline' && d.offline_since_ms ? `offline since ${fmtMs(d.offline_since_ms)}` : `last seen ${fmtAgo(d.last_seen_ms, now)}`}
                    {d.model ? ` · ${d.model}` : ''}
                  </p>
                  {(() => { const p = describePush(d.push); return p ? <p data-testid={`kiosk-device-push-${p.ok ? 'ok' : 'off'}`} style={{ fontSize: 12, color: p.ok ? GREEN : AMBER, marginTop: 2 }}>{p.text}</p> : null; })()}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => load()} data-testid="kiosk-device-refresh" style={iconBtn}><RefreshCw className="h-4 w-4" /></button>
                {d.status === 'active' ? (
                  <>
                    {/* 7.6: nudge the phone and watch for its heartbeat */}
                    <button onClick={checkNow} disabled={checking} data-testid="kiosk-device-check-now"
                      style={{ ...iconBtn, color: GREEN, borderColor: GREEN + '40', opacity: checking ? 0.6 : 1, cursor: checking ? 'wait' : 'pointer' }}>
                      <Radio className="h-4 w-4" /> {checking ? 'Checking…' : 'Check now'}
                    </button>
                    <button onClick={() => setPrompt('name')} data-testid="kiosk-device-rename" style={iconBtn}><Pencil className="h-4 w-4" /> Rename</button>
                    <button onClick={() => setPrompt('site_name')} data-testid="kiosk-device-site" style={iconBtn}><MapPin className="h-4 w-4" /> Site</button>
                    <button onClick={() => setConfirmRevoke(true)} data-testid="kiosk-device-revoke" style={{ ...iconBtn, color: '#FF2D55', borderColor: '#FF2D5540' }}><Ban className="h-4 w-4" /> Revoke</button>
                  </>
                ) : (
                  <button onClick={() => setRePair(true)} data-testid="kiosk-device-repair" style={{ ...iconBtn, color: GREEN, borderColor: GREEN + '40' }}><Link2 className="h-4 w-4" /> Pair again</button>
                )}
              </div>
            </div>

            {/* Court: tenant link, public link, staff PIN */}
            <div className="grid md:grid-cols-3 gap-4 mb-4" style={card} data-testid="kiosk-device-court">
              <div>
                <p style={labelStyle}>Tenant</p>
                <div style={{ marginTop: 6 }}>
                  {d.status === 'active' ? (
                    <TenantSelect value={d.court_org_id} tenants={tenants} disabled={linking} onChange={setTenant} testId="kiosk-device-tenant" />
                  ) : <p style={{ fontSize: 14, color: d.court_name ? '#fff' : '#555' }}>{d.court_name || 'No tenant'}</p>}
                </div>
                <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Rows are stamped with the tenant linked when they arrive; relinking never rewrites history.</p>
              </div>
              <div>
                <p style={labelStyle}>Court link (permanent)</p>
                <div style={{ marginTop: 6 }}><CourtLinkCell device={d} compact={false} onRotate={() => setConfirmRotate(true)} /></div>
                <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Give this to the court. It opens with the phone&apos;s current staff PIN and shows only the court&apos;s side.</p>
              </div>
              <div>
                <p style={labelStyle}>Staff PIN on server</p>
                <div style={{ marginTop: 8, fontSize: 16 }}><StaffPinStatus device={d} now={now} /></div>
                <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>
                  {d.staff_pin_present ? 'The phone mirrors a salted hash of its staff PIN — never the PIN. A PIN change on the phone lands on its next heartbeat.'
                    : 'Until the phone shares its staff PIN (set one in the kiosk Settings and let it come online), the court link cannot be opened.'}
                </p>
              </div>
            </div>

            {/* Live state */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" style={card}>
              <Fact label="App" value={d.app_version ? `${d.app_version} (${d.version_code ?? '?'})` : '—'} testId="kiosk-device-app" />
              <Fact label="Battery" value={d.battery != null ? `${d.battery}%${d.charging ? ' · charging' : ''}` : '—'} testId="kiosk-device-battery"
                color={d.battery != null && d.battery <= 15 ? '#FF2D55' : '#fff'} />
              <Fact label="Rows waiting" value={d.rows_pending || 0} testId="kiosk-device-pending" color={d.rows_pending > 0 ? AMBER : '#fff'} />
              <div><p style={labelStyle}>Ledger vs server</p><div style={{ marginTop: 5, fontSize: 14 }}><SyncBadge state={d.sync_state} testId="kiosk-device-sync" /></div></div>
              <Fact label="Phone clock" value={d.skew_ms == null ? '—' : d.skew_flag ? `off by ${Math.round(d.skew_ms / 1000)} s` : 'in sync'} testId="kiosk-device-skew"
                color={d.skew_flag ? AMBER : '#fff'} />
              <Fact label="Paired" value={fmtMs(d.paired_at_ms)} testId="kiosk-device-paired" />
              <Fact label="Rate / hour" value={`₱${(d.config?.rate_centavos_per_hour || 0) / 100}`} testId="kiosk-device-rate" />
              <Fact label="HighMark share / hour" value={`₱${(d.config?.owner_share_centavos_per_hour || 0) / 100}`} testId="kiosk-device-share" />
              <Fact label="Out of service" value={d.out_of_service ? `Yes${d.config?.out_of_service_message ? ` — "${d.config.out_of_service_message}"` : ''}` : 'No'} testId="kiosk-device-oos" color={d.out_of_service ? AMBER : '#fff'} />
            </div>

            {/* Session — K8: the live card with expected figures while one runs */}
            {d.live ? (
              <div style={{ marginBottom: 16 }} data-testid="kiosk-device-session"><LiveCard live={d.live} audience="owner" testId="kiosk-device-live" /></div>
            ) : (
              <div style={{ ...card, marginBottom: 16 }} data-testid="kiosk-device-session">
                <p style={labelStyle}>Session</p>
                {d.session ? (
                  <p style={{ fontSize: 14, marginTop: 4 }}>
                    <span style={{ color: AMBER, fontWeight: 700 }}>Running</span> since {fmtMs(d.session.started_at_ms)}
                    {d.session.mode ? ` · ${d.session.mode}` : ''}
                    {d.session.ends_at_ms ? ` · ends ${fmtMs(d.session.ends_at_ms)}` : ' · open-ended'}
                  </p>
                ) : <p style={{ fontSize: 14, color: GREY, marginTop: 4 }}>Idle — no session reported on the last heartbeat</p>}
              </div>
            )}

            {/* Notes */}
            <div style={{ ...card, marginBottom: 16 }}>
              <div className="flex items-center justify-between">
                <p style={labelStyle}>Notes</p>
                {d.status === 'active' && <button onClick={() => setPrompt('notes')} data-testid="kiosk-device-notes-edit" style={{ ...iconBtn, padding: '4px 8px' }}><FileText className="h-3.5 w-3.5" /> Edit</button>}
              </div>
              <p style={{ fontSize: 13, color: d.notes ? '#ccc' : '#555', marginTop: 4, whiteSpace: 'pre-wrap' }}>{d.notes || 'Nothing yet.'}</p>
            </div>

            {/* Conflicts — a phone settlement over sessions already collected on the web (kept verbatim, never merged) */}
            {data.conflicts?.length > 0 && (
              <div style={{ ...card, marginBottom: 16, borderColor: '#FF2D55', background: 'rgba(255,45,85,0.06)' }} data-testid="kiosk-device-conflicts">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: '#FF2D55', marginBottom: 8 }}>
                  <AlertOctagon className="h-4 w-4 inline mr-2" style={{ verticalAlign: -2 }} />Double settlement — {data.conflicts.length}
                </h2>
                {data.conflicts.map(c => (
                  <p key={c.row_id} data-testid={`kiosk-conflict-${c.row_id}`} style={{ fontSize: 13, color: '#ffb3c0', padding: '6px 0', borderTop: '1px solid rgba(255,45,85,0.25)' }}>
                    Phone collection #{c.row_id} ({epochDayToYmd(c.row?.periodFromEpochDay)} to {epochDayToYmd(c.row?.periodToEpochDay)}, gross {fmtCentavos(c.row?.grossCentavos)}, collected {fmtMs(c.row?.collectedAt)})
                    covers {c.conflict_detail?.session_row_ids?.length || 0} session(s) already collected on the web ({(c.conflict_detail?.web_collection_ids || []).join(', ')}).
                    Flagged {fmtMs(c.conflict_at_ms)}. Both records stand; settle the difference by hand.
                  </p>
                ))}
              </div>
            )}

            {/* Reload now, and again once the nudge has had time to land (its record is written a few seconds after the command). */}
            <CommandBar device={d} onIssued={() => { load(true); setTimeout(() => load(true), 4000); setTimeout(() => load(true), 10000); }} />

            {d.status === 'active' && <CollectPanel device={d} onCollected={() => { load(true); setStatementsKey(k => k + 1); }} />}

            <BillingPanel kioskId={d.kiosk_id} />
            <StatementsSection kioskId={d.kiosk_id} refreshKey={statementsKey} />

            {data.web_collections?.length > 0 && (
              <div style={{ ...card, marginBottom: 16 }} data-testid="kiosk-device-web-collections">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Web settlements</h2>
                {data.web_collections.map(c => (
                  <p key={c.id} data-testid={`kiosk-web-collection-${c.id}`} style={{ fontSize: 13, padding: '6px 0', borderTop: '1px solid #1E1E1E' }}>
                    {epochDayToYmd(c.period_from_epoch_day)} → {epochDayToYmd(c.period_to_epoch_day)} · {c.session_count} rental(s) · gross <b>{fmtCentavos(c.gross_centavos)}</b>
                    <span style={{ color: GREY }}> · owner {fmtCentavos(c.owner_centavos)} · court {fmtCentavos(c.court_centavos)} (derived) · {fmtMs(c.collected_at_ms)} by {c.collected_by_name}</span>
                    {c.status !== 'committed' ? <span style={{ color: AMBER }}> · {c.status}</span> : c.acked_by_phone_at ? <span style={{ color: GREEN }}> · phone applied it</span> : <span style={{ color: AMBER }}> · waiting for the phone</span>}
                    {c.conflict && <span style={{ color: '#FF2D55', fontWeight: 700 }}> · CONFLICT</span>}
                    {c.note && <span style={{ color: GREY }}> · {c.note}</span>}
                  </p>
                ))}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* Timeline */}
              <div style={card} data-testid="kiosk-device-timeline">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Timeline</h2>
                {data.timeline.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#555' }}>No events yet.</p>
                ) : data.timeline.map((ev, i) => {
                  const meta = EVENT_META[ev.type] || { Icon: KeyRound, color: GREY, label: ev.type };
                  return (
                    <div key={i} className="flex items-start gap-3" style={{ padding: '9px 0', borderTop: i ? '1px solid #1E1E1E' : 'none' }} data-testid={`kiosk-event-${ev.type}`}>
                      <meta.Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: meta.color }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
                          {meta.label}
                          {ev.gap_ms != null && <span style={{ color: GREY, fontWeight: 400 }}> · silent {fmtDuration(ev.gap_ms)}</span>}
                        </p>
                        <p style={{ fontSize: 11, color: GREY }}>{fmtMs(ev.at_ms)}{ev.detail ? ` · ${ev.detail}` : ''}{ev.by ? ` · ${ev.by}` : ''}</p>
                      </div>
                    </div>
                  );
                })}
                <p style={{ fontSize: 11, color: '#444', marginTop: 10 }}>Server-side view. The phone&apos;s own connectivity log (why it was offline) is mirrored as rows; outages and stops are on the Review page.</p>
              </div>

              {/* Commands (Phase 3) */}
              <CommandHistory commands={data.commands || []} />
            </div>
          </>
        )}
      </main>

      {prompt && d && (
        <TextPromptModal
          title={prompt === 'name' ? 'Rename device' : prompt === 'site_name' ? 'Set site' : 'Notes'}
          label={prompt === 'name' ? 'Device name' : prompt === 'site_name' ? 'Site name' : 'Notes'}
          initial={d[prompt] || ''}
          required={prompt === 'name'}
          multiline={prompt === 'notes'}
          maxLength={prompt === 'notes' ? 500 : 60}
          testId={`kiosk-prompt-${prompt}`}
          onSave={v => saveField(prompt, v)}
          onClose={() => setPrompt(null)} />
      )}
      {confirmRevoke && d && (
        <ConfirmModal title={`Revoke ${d.name}?`} confirmLabel="Revoke"
          message="The phone keeps working offline with its own ledger, but it stops syncing and shows 'not paired' in Settings. You can pair it again later and its history stays. Enter the owner PIN or your authenticator code to confirm."
          input={{ value: ownerKey, onChange: v => setOwnerKey(v.replace(/\D/g, '').slice(0, 8)), placeholder: 'Owner PIN or authenticator code' }}
          onConfirm={doRevoke} onCancel={() => { setConfirmRevoke(false); setOwnerKey(''); }} />
      )}
      {confirmRotate && d && (
        <ConfirmModal title="Replace the court link?" color={AMBER} confirmLabel="Replace link"
          message={`The link is meant to be permanent. Only do this when the court or the owner asked for it: the current link (${d.court_link?.code || '—'}) stops opening immediately and the court needs the new one. Enter the owner PIN or your authenticator code to confirm.`}
          input={{ value: ownerKey, onChange: v => setOwnerKey(v.replace(/\D/g, '').slice(0, 8)), placeholder: 'Owner PIN or authenticator code' }}
          onConfirm={doRotate} onCancel={() => { setConfirmRotate(false); setOwnerKey(''); }} />
      )}
      {rePair && d && <PairDeviceDialog replace={d} tenants={tenants} onClose={() => setRePair(false)} onDone={() => { setRePair(false); load(true); }} />}
    </SuperAdminShell>
  );
}
